import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { Alert, Button, Input } from '@/components/ui';
import { useAuth } from '@/hooks/useAuth';
import { applyFieldErrors, errorMessage } from '@/hooks/useApiError';
import { dashboardFor, paths } from '@/routes/paths';
import { ApiError } from '@/types/api';
import { loginSchema } from '@/validations';

import { BrandPanel } from './BrandPanel';

/**
 * One sign-in form, two doors.
 *
 * `portal="user"` (/login) is for students and companies; `portal="admin"`
 * (/admin/login) is for platform administrators. The portal is sent with the
 * credentials and the API enforces it: the admin page turns away everyone
 * else exactly as a wrong password would, and the user page answers an
 * administrator with `ADMIN_SIGN_IN_REQUIRED`, which is shown as a link to
 * the right page.
 */
export function LoginPage({ portal = 'user' }) {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isAdmin = portal === 'admin';

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm({ resolver: zodResolver(loginSchema) });

  const from = location.state?.from?.pathname;

  async function onSubmit(values) {
    try {
      const session = await login({ ...values, portal });
      if (!session.user.is_email_verified) {
        // Signing in to an unverified account emails a fresh code.
        navigate(`${paths.verifyEmail}?email=${encodeURIComponent(session.user.email)}`, {
          state: { codeSent: true },
        });
        return;
      }
      // A page that sent them here to sign in wins; otherwise their dashboard.
      // The marketing home is never a destination after signing in.
      const destination =
        from && from !== paths.home
          ? from
          : isAdmin
            ? paths.admin.overview
            : dashboardFor(session.user.role);
      navigate(destination, { replace: true });
    } catch (error) {
      if (!applyFieldErrors(error, setError)) {
        setError('root', { message: errorMessage(error, 'Could not sign in') });
      }
      // A blocked account is a policy decision, not a bad password: say so.
      if (error instanceof ApiError && error.status === 403) {
        setError('root', {
          type: error.code === 'ADMIN_SIGN_IN_REQUIRED' ? 'admin' : 'forbidden',
          message: error.message,
        });
      }
    }
  }

  return (
    // `.auth-shell` re-points the brand ramp to the consoles' violet (see
    // `index.css`), so the button, links and focus rings below match the
    // admin, company and student screens without any change to the controls.
    <section className="auth-shell bg-surface-muted">
      <div className="app-container flex justify-center py-10 sm:py-16">
        <div className="rounded-panel shadow-card border-line grid w-full max-w-5xl overflow-hidden border lg:grid-cols-[5fr_6fr]">
          {isAdmin ? (
            <BrandPanel
              heading="Platform administration."
              points={[
                'Verify companies, students and documents',
                'Moderate internships and reported content',
                'Billing, reports and system settings',
              ]}
            />
          ) : (
            <BrandPanel />
          )}

          <div className="bg-surface flex flex-col justify-center p-6 sm:p-10 lg:p-12">
            <h1 className="text-ink text-2xl font-semibold tracking-tight">
              {isAdmin ? 'Admin sign in' : 'Welcome back'}
            </h1>
            <p className="text-ink-muted mt-2 text-sm">
              {isAdmin
                ? 'For platform administrators only.'
                : 'Sign in to continue your internship search.'}
            </p>

            <form onSubmit={handleSubmit(onSubmit)} className="mt-6 space-y-4" noValidate>
              {errors.root && (
                <Alert tone="error">
                  {errors.root.message}
                  {errors.root.type === 'admin' && (
                    <>
                      {' '}
                      <Link to={paths.admin.login} className="font-semibold underline">
                        Go to admin sign in
                      </Link>
                    </>
                  )}
                </Alert>
              )}

              <Input
                label="Email"
                type="email"
                autoComplete="email"
                required
                error={errors.email?.message}
                {...register('email')}
              />
              <Input
                label="Password"
                type="password"
                autoComplete="current-password"
                required
                error={errors.password?.message}
                {...register('password')}
              />

              <div className="flex justify-end">
                <Link to={paths.forgotPassword} className="text-brand-600 text-sm">
                  Forgot your password?
                </Link>
              </div>

              <Button type="submit" fullWidth isLoading={isSubmitting}>
                Sign in
              </Button>
            </form>

            {/* Administrators are created by another admin, never signed up. */}
            {!isAdmin && (
              <p className="text-ink-muted mt-6 text-center text-sm">
                New here?{' '}
                <Link to={paths.registerStudent} className="text-brand-600 font-medium">
                  Create an account
                </Link>
              </p>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
